import React, { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext();

const STORAGE_KEY = 'cp_cart';

const loadCart = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

// Cart key: variant id if provided, else product id (for legacy items without variants)
const cartKey = (item) => item.variant_id ? `v${item.variant_id}` : `p${item.id}`;

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const newKey = cartKey(action.payload);
      const existing = state.find(item => cartKey(item) === newKey);
      const qtyToAdd = action.payload.quantity || 1;
      if (existing) {
        return state.map(item =>
          cartKey(item) === newKey
            ? { ...item, quantity: item.quantity + qtyToAdd }
            : item
        );
      }
      return [...state, { ...action.payload, quantity: qtyToAdd, notes: action.payload.notes || '' }];
    }
    case 'REMOVE_ITEM':
      return state.filter(item => cartKey(item) !== action.payload);
    case 'UPDATE_QUANTITY':
      return state.map(item =>
        cartKey(item) === action.payload.key
          ? { ...item, quantity: Math.max(1, action.payload.quantity) }
          : item
      );
    case 'UPDATE_NOTES':
      return state.map(item =>
        cartKey(item) === action.payload.key
          ? { ...item, notes: action.payload.notes }
          : item
      );
    case 'CLEAR_CART':
      return [];
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, [], loadCart);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    dispatch({ type: 'ADD_ITEM', payload: product });
  };

  const removeFromCart = (key) => {
    dispatch({ type: 'REMOVE_ITEM', payload: key });
  };

  const updateQuantity = (key, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { key, quantity } });
  };

  const updateNotes = (key, notes) => {
    dispatch({ type: 'UPDATE_NOTES', payload: { key, notes } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart, addToCart, removeFromCart, updateQuantity, updateNotes, clearCart, cartCount, cartKey
    }}>
      {children}
    </CartContext.Provider>
  );
};

export { cartKey };

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};
